import { Link, useLocation } from "react-router-dom";
import { CONTACT_EMAIL, LOCATION } from "@/data/content";
import { KINGSLEY_INSTAGRAM, KINGSLEY_INSTAGRAM_HANDLE } from "@/data/media";
import { MaskedText } from "@/components/motion/Reveal";
import MotionToggle from "@/components/MotionToggle";

export default function Footer() {
  const year = new Date().getFullYear();
  const location = useLocation();

  if (location.pathname.startsWith("/kingsley") || location.pathname.startsWith("/maker")) {
    const isKingsley = location.pathname.startsWith("/kingsley");
    return (
      <footer
        data-testid="site-footer"
        className={`relative border-t border-hairline px-6 py-12 md:px-12 ${
          isKingsley ? "bg-[#F7F5F2]" : "bg-maker-dew"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <p className="overline text-muted">
            © {year} {isKingsley ? "M. Kingsley Studio" : "Phaedra Paulson · Maker"}
          </p>
          <div className="flex items-center gap-6">
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email" className="overline text-muted transition-colors hover:text-ink">
              {CONTACT_EMAIL}
            </a>
            {isKingsley ? (
              <a href={KINGSLEY_INSTAGRAM} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="overline text-muted transition-colors hover:text-ink">
                {KINGSLEY_INSTAGRAM_HANDLE}
              </a>
            ) : (
              <span className="overline text-muted">{LOCATION}</span>
            )}
            <Link to="/accessibility" data-testid="footer-accessibility" className="overline text-muted transition-colors hover:text-ink">Accessibility</Link>
            <MotionToggle testid="footer-motion-toggle" />
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer data-testid="site-footer" className="relative border-t border-hairline bg-paper px-6 pb-10 pt-24 md:px-12 md:pt-32">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-16 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="overline mb-6 text-muted">Available for commissions</p>
            <MaskedText
              as="p"
              lines={["Let’s make", "something together."]}
              className="font-display text-5xl font-extralight leading-[0.98] tracking-tight text-ink sm:text-6xl md:text-7xl"
            />
            <Link
              to="/contact"
              data-testid="footer-contact-cta"
              className="group mt-10 inline-flex items-center gap-3 border border-ink/40 px-8 py-4 transition-colors duration-500 hover:border-ink"
            >
              <span className="overline">Get in Touch</span>
              <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-8 md:col-span-5">
            <div>
              <p className="overline mb-4 text-muted">Contact</p>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email" className="link-underline block font-body text-sm">
                {CONTACT_EMAIL}
              </a>
              <p className="mt-3 font-body text-sm text-stone">{LOCATION}</p>
            </div>
            <div>
              <p className="overline mb-4 text-muted">Index</p>
              <ul className="space-y-2 font-body text-sm">
                <li><Link className="link-underline" to="/">Home</Link></li>
                <li><Link className="link-underline" to="/about">About</Link></li>
                <li><Link className="link-underline" to="/services">Services</Link></li>
                <li><Link className="link-underline" to="/gallery">Gallery</Link></li>
                <li>
                  <a
                    className="link-underline inline-flex items-center gap-1.5"
                    href="/phaedra-paulson-stylist-portfolio.pdf"
                    download
                    data-testid="footer-portfolio-download"
                  >
                    Portfolio <span aria-hidden>↓</span>
                  </a>
                </li>
                <li><Link className="link-underline" to="/studio">Studio</Link></li>
                <li><Link className="link-underline" to="/contact">Contact</Link></li>
                <li><Link className="link-underline" to="/accessibility" data-testid="footer-accessibility">Accessibility</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-hairline pt-6 md:flex-row md:items-center">
          <p className="overline text-muted">© {year} Phaedra Paulson</p>
          <div className="flex items-center gap-6">
            <MotionToggle testid="footer-motion-toggle" />
            <p className="overline text-muted">Artist — {LOCATION}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
